import * as React from "react";
import type { ComponentRenderProps } from "@json-render/react";
import { APP_COMPONENTS } from "../catalog";
import { isExternalHref, isSafeHref } from "../link";
import { readProps } from "../props";
import { IconByName } from "./icons";

/**
 * How an App view moves around its host app. In-app links go through the
 * host's navigator (a router `navigate`) so they never reload the page; with
 * none provided they fall back to a plain anchor.
 */
export type AppNavigate = (to: string) => void;

export const AppNavigateContext = React.createContext<AppNavigate | undefined>(
  undefined,
);

const LINK_CLASS =
  "inline-flex items-center gap-1 text-primary underline underline-offset-2 hover:opacity-80";

function isPlainLeftClick(event: React.MouseEvent): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

/**
 * A link to `href`: an in-app path via the host's navigator, anything else in
 * a new tab. An unsafe href (`javascript:`) renders as plain text.
 */
export function AppLink({
  href,
  children,
  className = LINK_CLASS,
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
}) {
  const navigate = React.use(AppNavigateContext);
  if (!isSafeHref(href)) return <span>{children}</span>;
  if (isExternalHref(href)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {children}
        <IconByName name="ArrowSquareOut" size="xs" />
      </a>
    );
  }
  return (
    <a
      href={href}
      className={className}
      onClick={(event) => {
        // Modified clicks keep the browser's open-in-new-tab behavior.
        if (!navigate || !isPlainLeftClick(event)) return;
        event.preventDefault();
        navigate(href);
      }}
    >
      {children}
    </a>
  );
}

export function LinkComponent({ element }: ComponentRenderProps) {
  const p = readProps(APP_COMPONENTS.Link.props, element.props);
  if (!p.href) return <span>{p.text ?? ""}</span>;
  return (
    <AppLink href={p.href}>
      {p.icon && <IconByName name={p.icon} />}
      {p.text ?? p.href}
    </AppLink>
  );
}
